import { Component, computed, inject, linkedSignal, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { httpResource } from "@angular/common/http"
import { ActivatedRoute, Router } from "@angular/router"
import { toSignal } from "@angular/core/rxjs-interop"
import { catchError, firstValueFrom, map, of, take } from "rxjs"
import { MatDialog } from "@angular/material/dialog"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { FormField, form, submit } from "@angular/forms/signals"
import {
    BENEFIT_FIELD_LABELS,
    Benefit,
    CategoryTypes,
    EmployeeMbrad,
    ModifyBenefit,
    ModifyBenefitFactory,
    ModifyObjectFactory,
} from "../../models"
import { BenefitsService, EmployeeService, ToastService } from "../../services"
import { JsonPatchDocumentGenerator } from "../../helpers/JsonPatchDocumentGenerator"
import { FiltersComponent } from "../../components/filters/filters.component"
import { FiltersValue, emptyFilters, fromDependencies, toDependencies } from "../../helpers/DependenciesForm"
import { BooleanInputComponent } from "../../components/boolean-input/boolean-input.component"
import { FileInputComponent } from "../../components/file-input/file-input.component"
import {
    CategoriesInputComponent,
    ConfirmDialogComponent,
    DictionaryInputComponent,
} from "../../components"
import {
    PATCH_IGNORED_FIELDS,
    describeError,
    invalidFieldsDetail,
    toServerFieldErrors,
} from "../../helpers/FormErrors"

@Component({
    selector: "valben-benefits-edit",
    templateUrl: "./benefits-edit.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        NgClass,
        MatProgressSpinnerModule,
        FormField,
        FiltersComponent,
        DictionaryInputComponent,
        BooleanInputComponent,
        FileInputComponent,
        CategoriesInputComponent,
    ],
})
export class BenefitsEditPage {
    private _benefitsService = inject(BenefitsService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)
    private _dialog = inject(MatDialog)
    private _patchGenerator = new JsonPatchDocumentGenerator()

    categoriesType = CategoryTypes.Benefit

    private _masterId = toSignal(inject(ActivatedRoute).paramMap.pipe(map(p => p.get("masterId") ?? "")), {
        initialValue: "",
    })

    benefit = httpResource<Benefit>(() => {
        const masterId = this._masterId()
        return masterId ? this._benefitsService.ByMasterIdUrl(masterId) : undefined
    })

    private _employees = toSignal(
        inject(EmployeeService).employees$.pipe(
            take(1),
            catchError(() => of([] as EmployeeMbrad[]))
        )
    )

    employeesReady = computed(() => this._employees() !== undefined)

    saving = signal(false)

    model = linkedSignal<ModifyBenefit>(() => {
        const benefit = this.benefit.value()
        return benefit ? ModifyBenefitFactory.from(benefit) : ModifyBenefitFactory.create()
    })

    benefitForm = form(this.model)

    filters = linkedSignal<FiltersValue>(() => {
        const benefit = this.benefit.value()
        const employees = this._employees()
        return benefit ? fromDependencies(benefit.dependencies, employees ?? []) : emptyFilters()
    })

    loading = computed(() => this.benefit.isLoading() || this.saving())
    loaded = computed(() => this.benefit.hasValue())

    async save(): Promise<void> {
        const original = this.benefit.value()
        if (!original) return

        this.benefitForm.dependencies().value.set(toDependencies(this.filters(), original.dependencies))
        this.saving.set(true)
        let failed = false

        const saved = await submit(this.benefitForm, {
            action: async f => {
                const patch = this._patchGenerator.getJsonPatchDocument(
                    ModifyBenefitFactory.from(original),
                    f().value(),
                    PATCH_IGNORED_FIELDS
                )

                try {
                    await firstValueFrom(
                        this._benefitsService.Modify(
                            this._masterId(),
                            ModifyObjectFactory.create(patch, original.version)
                        )
                    )
                    return null
                } catch (err) {
                    failed = true
                    const { detail, errors } = toServerFieldErrors(err, this.benefitForm, BENEFIT_FIELD_LABELS)
                    this._error(detail)
                    return errors
                }
            },
            onInvalid: () => this._error(invalidFieldsDetail(this.benefitForm, BENEFIT_FIELD_LABELS)),
        })

        this.saving.set(false)
        if (failed || !saved) return

        this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Uspješno izmijenjeno",
            life: 10000,
        })
        this.back()
    }

    async delete(): Promise<void> {
        this.saving.set(true)
        try {
            await firstValueFrom(this._benefitsService.Delete(this._masterId()))
            this._messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Uspješno obrisano",
                life: 10000,
            })
            this.back()
        } catch (err) {
            this._error(describeError(err).message)
        } finally {
            this.saving.set(false)
        }
    }

    async showDeleteModal(): Promise<void> {
        const confirmed = await firstValueFrom(
            this._dialog
                .open(ConfirmDialogComponent, {
                    width: "35rem",
                    data: { header: "Jeste li sigurni da želite obrisati entitet?" },
                })
                .afterClosed()
        )
        if (confirmed) await this.delete()
    }

    back(): void {
        this._router.navigate(["/benefits"])
    }

    private _error(detail: string): void {
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }
}
